import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  DEFAULT_AUTOREPLY_CONTACT,
  DEFAULT_AUTOREPLY_TRIAL,
} from "@heracles/shared";
import { api } from "../../api/client";
import type { Gym } from "../../api/hooks";
import { PhotoField } from "../../components/PhotoField";
import { OpeningHoursEditor, openingHoursErrors } from "../../components/admin/OpeningHoursEditor";
import { LoadingSection } from "../../components/Spinner";

// La route admin renvoie aussi les champs non exposés publiquement.
type AdminGym = Gym & {
  contactEmails: string[];
  autoReplyContact: string;
  autoReplyTrial: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AdminGyms() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "gyms"],
    queryFn: () => api<AdminGym[]>("/admin/gyms"),
  });
  const [editing, setEditing] = useState<AdminGym | null>(null);

  const cleanedEmails = (editing?.contactEmails ?? []).map((e) => e.trim()).filter(Boolean);
  const emailsValid = cleanedEmails.every((e) => EMAIL_RE.test(e));
  const hoursValid =
    !editing || Object.keys(openingHoursErrors(editing.openingHours)).length === 0;

  const update = useMutation({
    mutationFn: (gym: AdminGym) =>
      api(`/admin/gyms/${gym.id}`, {
        method: "PUT",
        json: {
          name: gym.name,
          address: gym.address,
          phone: gym.phone,
          email: gym.email,
          imageUrl: gym.imageUrl,
          contactEmails: (gym.contactEmails ?? []).map((e) => e.trim()).filter(Boolean),
          autoReplyContact: gym.autoReplyContact ?? "",
          autoReplyTrial: gym.autoReplyTrial ?? "",
          reservationUrl: gym.reservationUrl,
          reservationEmbedHtml: gym.reservationEmbedHtml,
          latitude: gym.latitude,
          longitude: gym.longitude,
          openingHours: gym.openingHours,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "gyms"] });
      qc.invalidateQueries({ queryKey: ["gyms"] });
      setEditing(null);
    },
  });

  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase">Salles</h1>
      {isLoading && <LoadingSection />}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {data?.map((g) => (
          <div key={g.id} className="card">
            <h2 className="font-display text-xl font-bold">{g.name}</h2>
            <p className="text-sm text-ink-500">{g.address}</p>
            <p className="text-sm">{g.phone}</p>
            <p className="mt-2 text-xs text-ink-500">
              {g.contactEmails?.length
                ? `${g.contactEmails.length} email(s) de réception`
                : "Aucun email de réception configuré"}
            </p>
            <button onClick={() => setEditing(g)} className="btn-outline mt-4">
              Éditer
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-[80vw] overflow-y-auto rounded-lg bg-white p-6">
            <h2 className="font-display text-xl font-bold">Éditer {editing.name}</h2>

            <div className="mt-4 grid gap-3">
              <Label>Nom</Label>
              <input
                className="input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <Label>Adresse</Label>
              <input
                className="input"
                value={editing.address}
                onChange={(e) => setEditing({ ...editing, address: e.target.value })}
              />
              <Label>Image de la salle</Label>
              <PhotoField
                label="Photo de la salle (page d'accueil)"
                value={editing.imageUrl ?? ""}
                onChange={(imageUrl) => setEditing({ ...editing, imageUrl })}
              />

              <div className="mt-6 border-t border-ink-200 pt-5">
                <Label>Heures d'ouverture</Label>
                <p className="mt-0.5 text-xs text-ink-500 mb-4">
                  Configurez les horaires d'ouverture pour chaque jour. Vous pouvez ajouter plusieurs plages par jour (ex: matin et soir).
                </p>
                <OpeningHoursEditor
                  value={editing.openingHours}
                  onChange={(openingHours) => setEditing({ ...editing, openingHours })}
                />
              </div>

              <Label>Téléphone</Label>
              <input
                className="input"
                value={editing.phone}
                onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
              />
              <Label>Email public (affiché sur le site)</Label>
              <input
                className="input"
                value={editing.email ?? ""}
                placeholder="contact@…"
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              />
              <Label>URL de réservation externe</Label>
              <input
                className="input"
                value={editing.reservationUrl}
                placeholder="URL de réservation externe"
                onChange={(e) => setEditing({ ...editing, reservationUrl: e.target.value })}
              />
              <Label>Iframe de réservation (facultatif)</Label>
              <textarea
                className="input"
                rows={3}
                value={editing.reservationEmbedHtml ?? ""}
                placeholder="<iframe ...> (facultatif)"
                onChange={(e) =>
                  setEditing({ ...editing, reservationEmbedHtml: e.target.value })
                }
              />
            </div>

            {/* ── Emails de réception des demandes ─────────────────────────── */}
            <div className="mt-6 border-t border-ink-200 pt-5">
              <p className="text-sm font-semibold text-ink-700">
                Emails de réception des demandes
              </p>
              <p className="mt-0.5 text-xs text-ink-500">
                Toute demande envoyée depuis le formulaire de contact ou d'essai pour cette
                salle est transmise à ces adresses. Laisse vide pour ne recevoir aucun email
                (les demandes restent visibles dans « Messages »).
              </p>

              <div className="mt-3 space-y-2">
                {(editing.contactEmails ?? []).length === 0 && (
                  <p className="text-xs italic text-ink-400">Aucune adresse configurée.</p>
                )}
                {(editing.contactEmails ?? []).map((value, i) => {
                  const invalid = value.trim() !== "" && !EMAIL_RE.test(value.trim());
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="email"
                        value={value}
                        onChange={(e) => {
                          const next = [...editing.contactEmails];
                          next[i] = e.target.value;
                          setEditing({ ...editing, contactEmails: next });
                        }}
                        placeholder="adresse@exemple.fr"
                        className={`input flex-1 ${invalid ? "border-brand-500" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            ...editing,
                            contactEmails: editing.contactEmails.filter((_, j) => j !== i),
                          })
                        }
                        className="btn-ghost px-3 py-2 text-brand-500"
                        aria-label="Supprimer cette adresse"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() =>
                  setEditing({ ...editing, contactEmails: [...(editing.contactEmails ?? []), ""] })
                }
                className="btn-outline mt-3"
              >
                + Ajouter une adresse
              </button>
              {!emailsValid && (
                <p className="mt-2 text-xs text-brand-500">Une des adresses est invalide.</p>
              )}
            </div>

            {/* ── Réponses automatiques ────────────────────────────────────── */}
            <div className="mt-6 border-t border-ink-200 pt-5">
              <p className="text-sm font-semibold text-ink-700">Réponses automatiques</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Message envoyé automatiquement au visiteur, de façon différée (3 à 12 min) pour
                un rendu naturel. Variables disponibles :{" "}
                <code className="rounded bg-ink-100 px-1">{"{{prenom}}"}</code>{" "}
                <code className="rounded bg-ink-100 px-1">{"{{salle}}"}</code>{" "}
                <code className="rounded bg-ink-100 px-1">{"{{creneau}}"}</code>. Laisse vide
                pour utiliser le texte par défaut.
              </p>

              <div className="mt-3">
                <Label>Formulaire de contact (demande d'info)</Label>
                <textarea
                  className="input font-mono text-xs"
                  rows={7}
                  value={editing.autoReplyContact ?? ""}
                  placeholder={DEFAULT_AUTOREPLY_CONTACT}
                  onChange={(e) =>
                    setEditing({ ...editing, autoReplyContact: e.target.value })
                  }
                />
              </div>

              <div className="mt-3">
                <Label>Formulaire de séance d'essai</Label>
                <textarea
                  className="input font-mono text-xs"
                  rows={7}
                  value={editing.autoReplyTrial ?? ""}
                  placeholder={DEFAULT_AUTOREPLY_TRIAL}
                  onChange={(e) => setEditing({ ...editing, autoReplyTrial: e.target.value })}
                />
              </div>
            </div>

            {!hoursValid && (
              <p className="mt-6 text-xs text-brand-500">
                Corrigez les heures d'ouverture invalides (l'heure de fermeture doit être après
                l'ouverture) avant d'enregistrer.
              </p>
            )}

            <div className="mt-6 flex gap-2">
              <button
                onClick={() => update.mutate(editing)}
                disabled={!emailsValid || !hoursValid || update.isPending}
                className="btn-primary disabled:opacity-50"
              >
                {update.isPending ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button onClick={() => setEditing(null)} className="btn-ghost">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">{children}</span>;
}
